import { useState } from 'react';
import { X, Trash2, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useGenerateTestCases, useSaveTestCases } from '../../hooks/useSpecifications';
import type { GeneratedTestCase } from '../../services/specificationService';
import Spinner from '../ui/Spinner';

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: '정상',
  NEGATIVE: '비정상',
  BOUNDARY: '경계값',
  ALTERNATIVE: '대안',
};

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

interface Props {
  projectId: string;
  specId: string;
  onClose: () => void;
}

export default function TestCaseGenerateModal({ projectId, specId, onClose }: Props) {
  const generateMutation = useGenerateTestCases(projectId);
  const saveMutation = useSaveTestCases(projectId);

  const [testCases, setTestCases] = useState<GeneratedTestCase[]>([]);
  const [generated, setGenerated] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const handleGenerate = async () => {
    try {
      const result = await generateMutation.mutateAsync(specId);
      setTestCases(result);
      setGenerated(true);
      if (result.length === 0) {
        toast('흐름 데이터가 없어 테스트케이스를 생성할 수 없습니다.', { icon: 'ℹ️' });
      }
    } catch {
      toast.error('테스트케이스 생성에 실패했습니다.');
    }
  };

  const handleRemoveTC = (index: number) => {
    setTestCases((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateTC = (index: number, field: keyof GeneratedTestCase, value: string) => {
    setTestCases((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async () => {
    if (testCases.length === 0) {
      toast.error('저장할 테스트케이스가 없습니다.');
      return;
    }
    try {
      await saveMutation.mutateAsync({ specId, testCases });
      toast.success(`${testCases.length}개 테스트케이스가 저장되었습니다.`);
      onClose();
    } catch {
      toast.error('테스트케이스 저장에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">테스트케이스 자동 생성</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {!generated ? (
            <div className="text-center py-10">
              <p className="text-sm text-gray-500 mb-4">
                기능 명세의 주요 흐름, 대안 흐름, 예외 흐름을 분석하여
                <br />
                테스트케이스를 자동으로 생성합니다.
              </p>
              <button
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {generateMutation.isPending ? (
                  <Spinner className="size-4 text-white" />
                ) : (
                  <CheckCircle className="size-4" />
                )}
                테스트케이스 생성
              </button>
            </div>
          ) : testCases.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-gray-500">생성된 테스트케이스가 없습니다.</p>
              <p className="text-sm text-gray-400 mt-1">흐름 단계를 먼저 작성해주세요.</p>
            </div>
          ) : (
            testCases.map((tc, i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* TC Header */}
                <div
                  className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                >
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${
                      tc.type === 'POSITIVE'
                        ? 'bg-green-100 text-green-800'
                        : tc.type === 'NEGATIVE'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {TYPE_LABEL[tc.type] ?? tc.type}
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                    {tc.title}
                  </span>
                  <span className="text-sm text-gray-500">
                    {PRIORITY_LABEL[tc.priority] ?? tc.priority}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTC(i);
                    }}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                  {expandedIdx === i ? (
                    <ChevronUp className="size-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="size-4 text-gray-400" />
                  )}
                </div>

                {/* TC Detail (expanded) */}
                {expandedIdx === i && (
                  <div className="px-4 py-3 space-y-3 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">제목</label>
                      <input
                        value={tc.title}
                        onChange={(e) => handleUpdateTC(i, 'title', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">기대 결과</label>
                      <input
                        value={tc.expectedResult}
                        onChange={(e) => handleUpdateTC(i, 'expectedResult', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">단계</label>
                      <div className="space-y-1">
                        {tc.steps.map((step, si) => (
                          <div key={si} className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="font-mono text-gray-400 w-4 text-right">{step.step}.</span>
                            <span className="flex-1">{step.action}</span>
                            <span className="text-gray-400">→ {step.expected}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            {generated ? `${testCases.length}개 테스트케이스` : ''}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            {generated && testCases.length > 0 && (
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saveMutation.isPending && <Spinner className="size-4 text-white" />}
                확정 ({testCases.length}개 저장)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
